<template>
  <v-card
    :class="['session-card', layout === 'list' ? 'session-card--list' : '']"
    @click="onCardClick(session)"
    :elevation="2"
  >
    <div
      class="session-card__banner session-banner d-flex flex-column gap-1"
      :style="{ backgroundImage: `url(${session.bannerImage || defaultBanner})` }"
    >
      <div class="d-flex flex-row justify-space-between">
        <span class="align-self-center pt-1">{{ formattedStartTime }}</span>
        <!-- <v-btn class="starting-soon-indicator" :color="statusColor" style="padding: 10px">
          {{ getStatusText(session.status) }}
        </v-btn> -->
      </div>
      <h2>{{ session.title }}</h2>
    </div>
    <div class="session-description d-flex flex-column justify-space-between">
      <p v-snip="{ lines: 3 }">{{ truncatedDescription }}</p>
      <m-user-preview :user="session.createdBy" />
    </div>
  </v-card>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import { format } from 'date-fns';
import { CommunitySessionStatus, ICommunitySession } from '@mentessa/types';
import MUserPreview from '@/components/MUserPreview';
import { mapGetters } from 'vuex';

export default Vue.extend({
  name: 'm-community-session-card',
  components: { MUserPreview },
  props: {
    session: Object as PropType<ICommunitySession>,
    layout: {
      type: String,
      default: 'grid',
      validator: (value: string) => ['grid', 'list'].includes(value),
    },
    onCardClick: Function,
  },

  data: () => ({
    maxDescriptionLength: 100,
  }),

  computed: {
    ...mapGetters('ui', {
      formatInUserTimeZone: 'formatInUserTimeZone',
    }),

    defaultBanner() {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAwIiBoZWlnaHQ9IjI2NyIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48bGluZWFyR3JhZGllbnQgeDE9IjUwJSIgeTE9IjEwMCUiIHgyPSI1MCUiIHkyPSIwJSIgaWQ9ImEiPjxzdG9wIHN0b3AtY29sb3I9IiMxRTg4RTUiIG9mZnNldD0iMCUiLz48c3RvcCBzdG9wLWNvbG9yPSIjMDBCQ0Q0IiBvZmZzZXQ9IjEwMCUiLz48L2xpbmVhckdyYWRpZW50PjwvZGVmcz48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGZpbGw9InVybCgjYSkiIGQ9Ik0wIDBoODAwdjI2N0gweiIvPjxwYXRoIGQ9Ik00MDAgNTlsOTIuODMgMTYwLjVIMzA3LjE3TDQwMCA1OXoiIHN0cm9rZT0iI0ZGRiIgc3Ryb2tlLXdpZHRoPSIyIiBvcGFjaXR5PSIuNSIvPjxjaXJjbGUgc3Ryb2tlPSIjRkZGIiBzdHJva2Utd2lkdGg9IjIiIG9wYWNpdHk9Ii41IiBjeD0iNTUwIiBjeT0iMTM4LjUiIHI9IjU1Ii8+PC9nPjwvc3ZnPg==';
    },

    formattedStartTime() {
      const formattedStart = this.formatInUserTimeZone(this.session.startTime, 'MMM d, H:mm');
      return `${formattedStart}`;
    },

    detailsRoute() {
      return {
        name: 'community-session-details',
        params: { id: this.session.id.toString() },
      };
    },

    organizerName() {
      return this.session.createdBy?.identity?.attributes?.firstName
        ? `${this.session.createdBy.identity.attributes.firstName} ${
            this.session.createdBy.identity.attributes.lastName || ''
          }`
        : 'Unknown Organizer';
    },

    organizerImage() {
      return this.session.createdBy?.identity?.attributes?.image || null;
    },

    statusColor() {
      switch (this.session.status) {
        case CommunitySessionStatus.Upcoming:
          return 'blue';
        case CommunitySessionStatus.Live:
          return 'success';
        case CommunitySessionStatus.Canceled:
          return 'error';
        case CommunitySessionStatus.Finished:
          return 'grey';
        default:
          return 'grey';
      }
    },

    formattedDate() {
      try {
        return format(new Date(this.session.date), 'MMM d, yyyy');
      } catch (error) {
        return 'Date not available';
      }
    },

    formattedTime() {
      try {
        const start = format(new Date(this.session.startTime), 'HH:mm');
        const end = format(new Date(this.session.endTime), 'HH:mm');
        return `${start} - ${end} (${this.session.timeZone})`;
      } catch (error) {
        return 'Time not available';
      }
    },

    participantsText() {
      const count = this.session.participants?.length || 0;
      const max = this.session.maximumAttendees || 0;
      return max > 0 ? `${count}/${max} participants` : `${count} participants`;
    },

    truncatedDescription() {
      if (!this.session.description) return '';
      if (this.session.description.length <= this.maxDescriptionLength) {
        return this.session.description;
      }
      return `${this.session.description.substring(0, this.maxDescriptionLength)}...`;
    },

    joinButtonText() {
      return this.session.status === CommunitySessionStatus.Live
        ? this.$t('session.join_now')
        : this.$t('session.subscribe');
    },
  },

  methods: {
    getStatusText(status: CommunitySessionStatus) {
      switch (status) {
        case CommunitySessionStatus.Upcoming:
          return this.$t('session.status.upcoming');
        case CommunitySessionStatus.Live:
          return this.$t('session.status.live');
        case CommunitySessionStatus.Canceled:
          return this.$t('session.status.canceled');
        case CommunitySessionStatus.Finished:
          return this.$t('session.status.finished');
        default:
          return status;
      }
    },

    joinSession() {
      // Here we would handle subscription or joining the session
      // This would typically dispatch an action to the store
      this.$emit('join', this.session);

      if (this.session.status === CommunitySessionStatus.Live && this.session.meetingUrl) {
        window.open(this.session.meetingUrl, '_blank');
      } else {
        this.$router.push(this.detailsRoute);
      }
    },
  },
});
</script>

<style lang="scss" scoped>
.session-card {
  height: 280px;
  width: 350px;

  // List view styling
  &--list {
    .session-card__content {
      flex-direction: row;
      height: 100%;
    }

    .session-card__banner-container {
      width: 200px;
      height: auto;
      min-height: 180px;
      flex-shrink: 0;
      height: 120px;
      padding: 16px;
    }

    .session-card__details-container {
      flex: 1;
    }

    .session-card__details {
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    }

    @media (max-width: 600px) {
      .session-card__content {
        flex-direction: column;
      }

      .session-card__banner-container {
        width: 100%;
        height: 140px;
      }
    }
  }
}

.starting-soon-indicator {
  color: #ffffff;
  padding: 10px;
}
.session-banner {
  height: 120px;
  padding: 16px;
}

.session-description {
  height: 160px;
  padding: 24px 16px 24px 16px;
}

h2 {
  font-size: 20px;
}
</style>
